import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { User, RefreshCwIcon, X, Play, Pause } from 'lucide-react';
import ImageZoomModal from '../../common/ImageZoomModal';
import { useTranslation } from 'react-i18next';
import { Message } from './types';
import PDFThumbnail from './PDFThumbnail';
import PDFViewer from './PDFViewer';

/** Convert a gs:// URI to a public HTTPS URL. Passthrough for anything else. */
const toHttpUrl = (src: string): string => {
  if (src && src.startsWith('gs://')) {
    const withoutScheme = src.slice('gs://'.length);
    const slashIdx = withoutScheme.indexOf('/');
    if (slashIdx !== -1) {
      const bucket = withoutScheme.slice(0, slashIdx);
      const object = withoutScheme.slice(slashIdx + 1);
      return `https://storage.googleapis.com/${bucket}/${object}`;
    }
  }
  return src;
};

// ── WhatsApp-style voice message player ──────────────────────────────────────
interface VoiceMessageProps {
  src: string;
  fileName?: string;
  mimeType?: string;
  transcript?: string;
  /** True while the transcript is being fetched (shows a loading indicator) */
  isTranscribing?: boolean;
}

const VoiceMessage: React.FC<VoiceMessageProps> = ({ src, fileName, mimeType, transcript, isTranscribing }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // 40 bars of random-ish heights, seeded from the filename so they're stable
  const bars = useRef<number[]>(
    Array.from({ length: 40 }, (_, i) => {
      const seed = (fileName || 'voice').charCodeAt(i % (fileName?.length || 5)) + i;
      return 20 + ((seed * 37 + i * 13) % 60); // range 20–80 %
    })
  );

  const formatDuration = (s: number) => {
    if (!isFinite(s) || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime);
    if (!audio.paused) {
      animFrameRef.current = requestAnimationFrame(tick);
    }
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const handleBarClick = (index: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = (index / bars.current.length) * duration;
    setCurrentTime(audio.currentTime);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => { setIsPlaying(true); animFrameRef.current = requestAnimationFrame(tick); };
    const onPause = () => { setIsPlaying(false); if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
    const onLoaded = () => setDuration(audio.duration);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('loadedmetadata', onLoaded);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('loadedmetadata', onLoaded);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [tick]);

  const progress = duration > 0 ? currentTime / duration : 0;
  const activeBars = Math.round(progress * bars.current.length);

  return (
    <div className="flex flex-col gap-2 w-full max-w-[300px]">
      {/* Player row */}
      <div className="flex items-center gap-2.5 bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-2.5 border border-white/20">
        {/* Play / Pause button */}
        <button
          onClick={togglePlay}
          className="flex-shrink-0 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all shadow-sm active:scale-95"
        >
          {isPlaying
            ? <Pause className="w-3.5 h-3.5 text-gray-700 fill-gray-700" />
            : <Play className="w-3.5 h-3.5 text-gray-700 fill-gray-700 ml-0.5" />
          }
        </button>

        {/* Waveform + time */}
        <div className="flex-1 flex flex-col gap-1.5 min-w-0">
          {/* Waveform bars */}
          <div className="flex items-end gap-[2px] cursor-pointer" style={{ height: '28px' }}>
            {bars.current.map((h, i) => (
              <div
                key={i}
                onClick={() => handleBarClick(i)}
                className="rounded-full transition-colors duration-75"
                style={{
                  flex: '1 1 0',
                  minWidth: '2px',
                  height: `${h}%`,
                  backgroundColor: i < activeBars
                    ? 'rgba(255,255,255,1)'
                    : 'rgba(255,255,255,0.35)',
                }}
              />
            ))}
          </div>
          {/* Timer */}
          <span className="text-[10px] text-white/60 leading-none tabular-nums">
            {formatDuration(isPlaying || currentTime > 0 ? currentTime : duration)}
          </span>
        </div>

        {/* Hidden native audio */}
        <audio ref={audioRef} src={src} preload="metadata" className="hidden" />
      </div>

      {/* Transcript area */}
      {isTranscribing && (
        <div className="flex items-center gap-1.5 px-1">
          {/* Animated dots */}
          <span className="flex gap-0.5">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className="w-1 h-1 rounded-full bg-white/50 animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </span>
          <span className="text-[11px] text-white/55 italic leading-none">
            {t('chat.audio.transcribing', 'Transcribing…')}
          </span>
        </div>
      )}
      {!isTranscribing && transcript && (
        <p className="text-[12px] text-white/90 leading-relaxed break-words px-1 border-l-2 border-white/30 pl-2">
          {transcript}
        </p>
      )}
    </div>
  );
};

interface UserMessageProps {
  message: Message;
  isLoading: boolean;
  isLastMessage?: boolean;
  expandedErrors: Set<string>;
  errorTooltipRef: React.RefObject<HTMLDivElement | null>;
  onRetry: (message: Message) => void;
  onToggleErrorDetails: (messageId: string) => void;
  onCopyErrorDetails: (errorDetails: any, messageId: string) => void;
  formatTime: (date: Date) => string;
  userPhotoURL?: string | null;
  userDisplayName?: string | null;
}

const UserMessage: React.FC<UserMessageProps> = React.memo(({
  message,
  isLoading,
  isLastMessage = false,
  expandedErrors,
  errorTooltipRef,
  onRetry,
  onToggleErrorDetails,
  onCopyErrorDetails,
  formatTime,
  userPhotoURL,
  userDisplayName,
}) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomedImageSrc, setZoomedImageSrc] = useState<string>('');
  const [zoomedImageMimeType, setZoomedImageMimeType] = useState<string>('');
  const hasRetriedRef = useRef(false);

  // Auto-retrigger SYSTEM_ACTION messages when they are the last message and streaming has stopped
  useEffect(() => {
    if (
      isLastMessage &&
      !isLoading &&
      message.text?.includes('SYSTEM_ACTION:') &&
      !hasRetriedRef.current
    ) {
      hasRetriedRef.current = true;
      // onRetry(message);
    }
  }, [isLastMessage, isLoading, message, onRetry]);

  // Reset retry guard when this message is no longer the last message
  useEffect(() => {
    if (!isLastMessage) {
      hasRetriedRef.current = false;
    }
  }, [isLastMessage]);

  // Hide messages containing "NEXT", SYSTEM_ACTION, SYSTEM_MESSAGE, or SYSTEM_RETRY (e.g. refine/retry messages)
  if (
    message.text?.includes('SYSTEM_ACTION:') ||
    message.text?.includes('NEXT') ||
    message.text?.includes('SYSTEM_MESSAGE:') ||
    message.text?.includes('SYSTEM_RETRY:')
  ) {
    return null;
  }
  
  return (
    <>
      <div className="flex gap-2 sm:gap-3 flex-row-reverse max-w-full">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center flex-shrink-0 bg-green-500 text-white overflow-hidden">
          {userPhotoURL ? (
            <img
              src={userPhotoURL}
              alt={userDisplayName || 'User'}
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-4 h-4 sm:w-5 sm:h-5" />
          )}
        </div>

        <div className="flex-1 min-w-0 sm:flex-none sm:max-w-xs lg:max-w-md xl:max-w-lg p-3 sm:p-4 rounded-2xl shadow-sm bg-gradient-to-r from-primary-500 to-secondary-500 text-white break-words overflow-hidden">
        
        {/* Show multiple uploaded files */}
        {message.uploadedFiles && message.uploadedFiles.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {message.uploadedFiles.map((file, index) => {
              // For PDFs, construct the full data URL from base64 data if not already present
              const pdfDataUrl = file.mimeType === 'application/pdf'
                ? (file.data.startsWith('gs://')
                    ? toHttpUrl(file.data)
                    : (file.data.startsWith('data:') ? file.data : `data:${file.mimeType};base64,${file.data}`))
                : null;

              // Audio files
              if (file.mimeType.startsWith('audio/')) {
                const audioSrc = file.data.startsWith('data:')
                  ? file.data
                  : `data:${file.mimeType};base64,${file.data}`;
                return (
                  <div key={index} className="w-full">
                    <VoiceMessage src={audioSrc} fileName={file.name} mimeType={file.mimeType} transcript={file.transcript} isTranscribing={file.isTranscribing} />
                  </div>
                );
              }
              
              return (
                <div key={index} className="relative">
                  {file.mimeType === 'application/pdf' ? (
                    <PDFThumbnail 
                      file={pdfDataUrl!}
                      onClick={() => {
                        setZoomedImageSrc(pdfDataUrl!);
                        setZoomedImageMimeType(file.mimeType);
                        setIsImageZoomed(true);
                      }}
                      maxHeight={200}
                    />
                  ) : (
                    <img
                      src={toHttpUrl(file.preview || file.data)}
                      alt={file.name || `File ${index + 1}`}
                      className="max-w-full h-auto rounded-lg shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ maxHeight: '200px', width: 'auto', objectFit: 'contain' }}
                      onClick={() => {
                        setZoomedImageSrc(toHttpUrl(file.preview || file.data));
                        setZoomedImageMimeType(file.mimeType);
                        setIsImageZoomed(true);
                      }}
                    />
                  )}
                  {file.name && (
                    <div className="mt-1 text-xs text-white/80 truncate max-w-[200px]">
                      {file.name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Message text */}
        {message.text && (
          <div
            className="whitespace-pre-wrap leading-relaxed text-message font-jakarta"
            dangerouslySetInnerHTML={{ __html: message.text }}
          />
        )}

        <span className="text-meta font-jakarta mt-2 block text-white/70">
          {formatTime(message.timestamp)}
        </span>




      </div>
      </div>

      {isImageZoomed && zoomedImageSrc && zoomedImageMimeType !== 'application/pdf' && (
        <ImageZoomModal src={zoomedImageSrc} alt="Zoomed uploaded image" onClose={() => setIsImageZoomed(false)} />
      )}
      {isImageZoomed && zoomedImageSrc && zoomedImageMimeType === 'application/pdf' && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setIsImageZoomed(false)}
        >
          <div className="relative max-w-7xl max-h-full w-full">
            <button
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 transition-colors z-10"
              onClick={() => setIsImageZoomed(false)}
            >
              <X className="w-6 h-6" />
            </button>
            <div className="max-h-[90vh] overflow-auto p-8" onClick={(e) => e.stopPropagation()}>
              <div className="bg-white rounded-lg p-6">
                <PDFViewer file={zoomedImageSrc} maxWidth={750} />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

export default UserMessage;
